import { Link } from 'react-router-dom';
import {
  Phone,
  Users,
  FileQuestion,
  RotateCw,
  ListChecks,
  Settings as SettingsIcon,
  ExternalLink,
  Volume2,
  Hash,
  Mic,
  ListTree,
  PhoneForwarded,
  Table2,
  UsersRound,
  Clock
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

function Section({
  id,
  icon,
  title,
  children
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card id={id}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="rounded-md bg-primary/10 p-1.5 text-primary">{icon}</div>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
        {children}
      </CardContent>
    </Card>
  );
}

const TOC = [
  { id: 'overview', label: 'סקירה כללית' },
  { id: 'trial', label: 'ניסיון ושדרוג' },
  { id: 'customers', label: 'ניהול לקוחות' },
  { id: 'questionnaires', label: 'שאלונים ושאלות' },
  { id: 'menu', label: 'תפריט שלוחה' },
  { id: 'routing', label: 'יעד לאחר שאלון' },
  { id: 'cycles', label: 'מחזורי איסוף' },
  { id: 'records', label: 'רשומות וייצוא' },
  { id: 'tables', label: 'טבלאות וייצוא לאקסל' },
  { id: 'settings', label: 'הגדרות' },
  { id: 'admins', label: 'כתובות התחברות' },
  { id: 'yemot', label: 'חיבור לימות המשיח' },
  { id: 'flow', label: 'דוגמה לשיחה' }
];

export default function Help() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[16rem_1fr]">
      <aside className="lg:sticky lg:top-20 lg:self-start">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">תוכן עניינים</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <nav className="flex flex-col gap-1">
              {TOC.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="rounded-md px-2 py-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </CardContent>
        </Card>
      </aside>

      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">מדריך שימוש</h1>
          <p className="text-sm text-muted-foreground">
            איך להפעיל את Easy Call מקצה לקצה - מהגדרת ארגון ועד איסוף הנתונים בפועל.
          </p>
        </div>

        <Section id="overview" icon={<Phone className="h-4 w-4" />} title="סקירה כללית">
          <p>
            <strong>Easy Call</strong> היא מערכת לאיסוף נתונים אוטומטי דרך מערכת טלפונית של ימות
            המשיח. במקום שלקוחות יישלחו טפסים או יתקשרו למוקד אנושי - הם פשוט מחייגים, המערכת
            מזהה אותם לפי מספר הטלפון, שואלת סדרת שאלות, ושומרת את התשובות אוטומטית במסד
            הנתונים.
          </p>
        </Section>

        <Section id="trial" icon={<Clock className="h-4 w-4" />} title="ניסיון ושדרוג">
          <p>
            כל חשבון חדש נפתח אוטומטית כ<strong>חשבון ניסיון ל-72 שעות</strong> - מתחברים עם Google
            ומקבלים ארגון חדש לשימוש מיידי, ללא צורך באישור מראש.
          </p>
          <ul className="list-disc space-y-1 ps-5">
            <li>
              במהלך הניסיון מוצג באנר עם הזמן שנותר, וכל הכלים פתוחים לשימוש מלא.
            </li>
            <li>
              <strong>בתום הניסיון</strong> המערכת ננעלת (כולל איסוף ה-IVR) עד לשדרוג, ומוצג מסך שדרוג
              עם פרטי יצירת קשר.
            </li>
            <li>
              <strong>שדרוג:</strong> המחיר הוא 36 ₪ לחודש - מחיר קבוע, ללא הגבלת שאלונים. השדרוג
              מתבצע בתיאום מול שירות הלקוחות, שמפעיל את החשבון.
            </li>
          </ul>
        </Section>

        <Section id="customers" icon={<Users className="h-4 w-4" />} title="ניהול לקוחות">
          <p>
            כל לקוח הוא מספר טלפון אחד בארגון. אפשר להוסיף ידנית, לייבא מאקסל, או לאפשר ללקוחות
            להתווסף אוטומטית בעת השיחה הראשונה (אז הם נוצרים ללא שם).
          </p>
          <ul className="list-disc space-y-1 ps-5">
            <li>
              <strong>הוסף לקוח:</strong> טלפון בלבד או טלפון + שם.
            </li>
            <li>
              <strong>ייבוא מ-Excel:</strong> הורד את התבנית עם הכפתור "הורד תבנית", מלא את
              העמודות <code>phone</code> ו-<code>name</code>, והעלה. שורה עם טלפון קיים תעדכן את
              השם.
            </li>
            <li>
              <strong>זיהוי בשיחה:</strong> אם הטלפון מוכר - המערכת תאמר "בוקר טוב {'{name}'}".
              אם לא - תיצור לקוח חדש ותגיד "בוקר טוב". (ניתן לערוך את התבניות בעמוד הגדרות.)
            </li>
            <li>
              <strong>מחיקת לקוח:</strong> מוחקת בקסקייד את כל הרשומות שלו. לא ניתן לבטל.
            </li>
          </ul>
        </Section>

        <Section
          id="questionnaires"
          icon={<FileQuestion className="h-4 w-4" />}
          title="שאלונים ושאלות"
        >
          <p>
            <strong>שאלון</strong> הוא רצף שאלות שייאסף בשיחה. אפשר ליצור כמה שאלונים פעילים
            במקביל - אם יש יותר מאחד, המערכת תציע למתקשר לבחור: "ל{'{ivr_name1}'} הקש 1, ל
            {'{ivr_name2}'} הקש 2".
          </p>
          <p>
            לכל שאלה יש <strong>סוג קלט</strong> - בחר את המתאים:
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-2 mb-1">
                <Hash className="h-4 w-4 text-blue-600" />
                <strong>הקשה (tap)</strong>
              </div>
              <p className="text-xs">
                קלט DTMF - מספרים ו-#. ניתן להגדיר min_digits, max_digits, ספרות מותרות, ועוד.
              </p>
            </div>
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-2 mb-1">
                <Mic className="h-4 w-4 text-purple-600" />
                <strong>דיבור (stt)</strong>
              </div>
              <p className="text-xs">
                קלט קולי, המרה לטקסט. דורש יחידות במערכת ימות המשיח.
              </p>
            </div>
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-2 mb-1">
                <Volume2 className="h-4 w-4 text-muted-foreground" />
                <strong>השמעה</strong>
              </div>
              <p className="text-xs">
                ללא קלט - רק להשמיע טקסט (כותרת/הסבר) ולעבור לשאלה הבאה.
              </p>
            </div>
          </div>
          <ul className="list-disc space-y-1 ps-5">
            <li>
              <strong>אישור קלט:</strong> אופציונלי - לאחר תשובה, המערכת תקריא "הקלט שלך הוא
              {' {value}'}, לאישור הקש 1 לתיקון 2".
            </li>
            <li>
              <strong>סדר שאלות:</strong> לחץ והחזק על האייקון <kbd>⋮⋮</kbd> בתחילת השורה כדי
              לגרור ולשנות סדר.
            </li>
            <li>
              <strong>מפתח (key):</strong> מזהה אנגלי שמופיע כשם עמודה בייצוא Excel -
              <code className="mx-1">monthly_usage</code> וכו'.
            </li>
          </ul>
        </Section>

        <Section id="menu" icon={<ListTree className="h-4 w-4" />} title="תפריט שלוחה">
          <p>
            בעמוד <strong>הגדרות → תפריט שלוחה</strong> ניתן להגדיר תפריט הפניות בכניסה לשיחה. כשהוא
            מופעל, הוא <strong>מחליף את בורר השאלונים האוטומטי</strong> - כל מקש מפנה ליעד שתבחר. כך
            אפשר להשתמש ב-Easy Call כשלוחת תפריט במערכת, גם בלי שום שאלון.
          </p>
          <ul className="list-disc space-y-1 ps-5">
            <li>
              <strong>סוגי יעד למקש:</strong> שאלון, שלוחה במערכת ימות (למשל <code>5</code> או{' '}
              <code>1/2</code>), ניתוק, או חזרה לשלוחה ראשית.
            </li>
            <li>
              <strong>הודעת פתיחה:</strong> אם תשאיר ריק - המערכת תבנה הודעה אוטומטית לפי הפריטים: "ל
              {'{תיאור}'} הקש 1, ל{'{תיאור}'} הקש 2". אם תגדיר הודעה משלך - היא תושמע כמו שהיא.
            </li>
            <li>
              <strong>תיאור:</strong> לשאלון ברירת המחדל היא שם ההשמעה (ivrName); לשלוחה/ניתוק/ראשית יש
              להזין תיאור לטקסט האוטומטי.
            </li>
            <li>
              <strong>סדר:</strong> מספר המקש נקבע לפי סדר הפריטים (חיצי מעלה/מטה לשינוי). עד 9 מקשים.
            </li>
          </ul>
        </Section>

        <Section
          id="routing"
          icon={<PhoneForwarded className="h-4 w-4" />}
          title="יעד לאחר שאלון"
        >
          <p>
            ניתן להגדיר לאן תועבר השיחה לאחר סיום שאלון (וגם ביציאות מוקדמות כמו "כבר נקלטה רשומה" או
            "אין מחזור פתוח").
          </p>
          <ul className="list-disc space-y-1 ps-5">
            <li>
              <strong>הגדרה כלל-ארגונית</strong> בעמוד <strong>הגדרות → יעד לאחר שאלון</strong>: ניתוק
              השיחה, חזרה לשלוחה ראשית, או מעבר לשלוחה ספציפית.
            </li>
            <li>
              <strong>דריסה לכל שאלון</strong> בטופס עריכת השאלון - שאלון מסוים יכול להגדיר יעד משלו, או
              לרשת את ברירת המחדל של הארגון.
            </li>
            <li>
              ברירת המחדל היא <strong>ניתוק</strong> בסיום השאלון.
            </li>
          </ul>
        </Section>

        <Section id="cycles" icon={<RotateCw className="h-4 w-4" />} title="מחזורי איסוף">
          <p>
            מחזור (cycle) הוא חלון זמן שבו ניתן לקלוט רשומות. כברירת מחדל, לקוח לא יכול להיכנס
            פעמיים לאותו מחזור (אם הוא מנסה - תושמע ההודעה "כבר נקלטה רשומה למחזור זה"). ניתן
            לאפשר עדכון בהגדרות.
          </p>
          <p>בחר את הקצב המתאים בעמוד <strong>הגדרות → קצב איסוף</strong>:</p>
          <ul className="list-disc space-y-1 ps-5">
            <li>
              <strong>ידני:</strong> מנהל פותח/סוגר מחזור בעמוד "מחזורים". שיחה לפני פתיחה תקבל
              "אין כעת מחזור פתוח".
            </li>
            <li>
              <strong>יומי / שבועי / חודשי (לועזי):</strong> מחזור חדש נפתח אוטומטית בכל
              תחילת תקופה.
            </li>
            <li>
              <strong>חודשי (עברי):</strong> מחזור חדש בכל ראש חודש עברי (לפי לוח Hebcal).
            </li>
            <li>
              <strong>ללא הגבלה:</strong> אין מחזורים - כל שיחה יוצרת רשומה חדשה.
            </li>
          </ul>
        </Section>

        <Section id="records" icon={<ListChecks className="h-4 w-4" />} title="רשומות וייצוא">
          <p>
            עמוד הרשומות מציג את כל ה-sessions שהושלמו, עם עמודה דינמית לכל שאלה בשאלון הנבחר.
          </p>
          <ul className="list-disc space-y-1 ps-5">
            <li>
              <strong>מסננים:</strong> שאלון, טווח תאריכים, מחזור.
            </li>
            <li>
              <strong>סטטוס:</strong> כברירת מחדל מוצגות רק רשומות שהושלמו. ניתן להפעיל את
              המתג כדי לראות גם נטושות (in_progress / abandoned).
            </li>
            <li>
              <strong>ייצוא Excel:</strong> כפתור "ייצא לאקסל" מוריד את כל התוצאות המסוננות (לא רק מה
              שמוצג במסך) בקובץ בשם{' '}
              <code>records-{'{ארגון}'}-{'{שאלון}'}-{'{תקופה}'}-{'{תאריך-שעה}'}.xlsx</code>, כולל שורת
              סיכום לעמודות מספריות ובלוק כותרת עם שם התוכנה, הארגון וחותמת הזמן.
            </li>
          </ul>
        </Section>

        <Section id="tables" icon={<Table2 className="h-4 w-4" />} title="טבלאות וייצוא לאקסל">
          <p>
            כל הטבלאות במערכת (לקוחות, רשומות, מחזורים, שאלונים, ארגונים) חולקות רכיב טבלה אחיד עם
            התנהגות נוחה:
          </p>
          <ul className="list-disc space-y-1 ps-5">
            <li>
              <strong>ייצוא לאקסל מכל טבלה:</strong> כפתור "ייצא לאקסל" יוצר קובץ <code>.xlsx</code>{' '}
              מותאם-עברית (טלפונים נשמרים כטקסט עם האפס המוביל), כולל בלוק כותרת עם שם התוכנה, הארגון
              וחותמת זמן - ושם הקובץ כולל את ה-slug של הארגון ותאריך.
            </li>
            <li>
              <strong>כותרת קפואה:</strong> שורת הכותרת נשארת קבועה בזמן גלילה.
            </li>
            <li>
              <strong>גובה אוטומטי:</strong> הטבלה ממלאת את הגובה הפנוי והתוכן נגלל בתוכה - העמוד עצמו
              לא נגלל בגלל גובה הטבלה.
            </li>
            <li>
              <strong>הצגת כל הרשומות:</strong> בלקוחות נטענים כל הלקוחות (לא רק עשרות), עם חיפוש מקומי
              ואפשרות לעבור בין "הצג הכל" לתצוגה בעמודים.
            </li>
            <li>
              <strong>מותאם לנייד:</strong> טבלאות רחבות נגללות אופקית, והתפריט העליון מתקפל לכפתור
              בנייד.
            </li>
          </ul>
        </Section>

        <Section id="settings" icon={<SettingsIcon className="h-4 w-4" />} title="הגדרות">
          <ul className="list-disc space-y-1 ps-5">
            <li>
              <strong>קצב איסוף + week_start:</strong> כפי שתואר בסעיף "מחזורים".
            </li>
            <li>
              <strong>תבניות הודעות IVR:</strong> ברכה (עם / בלי שם), הודעת סיום, "כבר נקלטה"
              וכו'. תווים אסורים (כמו נקודה) יוסרו אוטומטית בהקראה.
            </li>
            <li>
              <strong>גרף דשבורד:</strong> אופציונלית, בחר שאלת tap מספרית מתוך שאלון נבחר -
              הסכום שלה יוצג ככרטיסייה ובגרף.
            </li>
            <li>
              <strong>תפריט שלוחה ויעד לאחר שאלון:</strong> ראה הסעיפים "תפריט שלוחה" ו"יעד לאחר שאלון"
              למעלה.
            </li>
            <li>
              <strong>כתובות מייל להתחברות:</strong> ניהול המשתמשים שיכולים להיכנס לארגון (ראה הסעיף
              הבא).
            </li>
            <li>
              <strong>טוקן IVR:</strong> לחץ "הצג" כדי לראות את הטוקן, או "צור טוקן חדש" אם הוא
              דלף (זה דורש עדכון מיידי בימות המשיח).
            </li>
          </ul>
        </Section>

        <Section id="admins" icon={<UsersRound className="h-4 w-4" />} title="כתובות מייל להתחברות">
          <p>
            בעמוד <strong>הגדרות → כתובות מייל להתחברות</strong> ניתן לנהל מי יכול להיכנס ולנהל את
            הארגון - אפשר לצרף כמה חשבונות Google לאותו ארגון.
          </p>
          <ul className="list-disc space-y-1 ps-5">
            <li>
              <strong>הוספה:</strong> הקלד כתובת מייל ולחץ "הוסף". מאותו רגע החשבון יוכל להתחבר ולראות
              את הארגון.
            </li>
            <li>
              <strong>הסרה:</strong> לחיצה על אייקון הפח. לא ניתן להסיר את המנהל היחיד שנותר (כדי לא
              לנעול את הארגון).
            </li>
            <li>
              היכולת קיימת גם בצד ניהול-העל (מנהל מערכת), וגם כאן - בתוך הארגון עצמו.
            </li>
          </ul>
        </Section>

        <Section
          id="yemot"
          icon={<ExternalLink className="h-4 w-4" />}
          title="חיבור לימות המשיח"
        >
          <p>
            בעמוד <strong>הגדרות</strong> תמצא תיבת "קוד מלא לשלוחת API". העתק את כל הבלוק
            (~6 שורות) והדבק במערכת ניהול ימות המשיח:
          </p>
          <ol className="list-decimal space-y-1 ps-5">
            <li>היכנס ל-<a href="https://www.call2all.co.il" target="_blank" rel="noreferrer" className="underline">www.call2all.co.il</a> ולוח הניהול שלך.</li>
            <li>
              לך לשלוחה הרלוונטית ← לשונית <strong>מערכת תוכן</strong> ←{' '}
              <strong>הגדרות מתקדמות</strong>.
            </li>
            <li>מחק את כל התוכן הקיים והדבק את הקוד.</li>
            <li>שמור.</li>
          </ol>
          <p>
            הקוד מכיל <code>type=api</code>, את ה-URL הייעודי לארגון שלך, וגם{' '}
            <code>api_url_post=yes</code> ו-<code>verify_phone_operator_network=yes</code>.
          </p>
        </Section>

        <Section id="flow" icon={<Phone className="h-4 w-4" />} title="דוגמה לשיחה טיפוסית">
          <ol className="list-decimal space-y-2 ps-5">
            <li>לקוח מחייג למספר השלוחה במערכת ימות המשיח.</li>
            <li>
              ימות מבצעת קריאת API ל-Easy Call (URL הכולל את ה-token של הארגון). השרת מאמת
              את ה-token.
            </li>
            <li>
              השרת מזהה את הלקוח לפי מספר הטלפון. אם הוא מוכר - תושמע "בוקר טוב {'{name}'}"; אם
              לא - נוצר לקוח חדש ותושמע ההודעה "בוקר טוב".
            </li>
            <li>
              אם הוגדר <strong>תפריט שלוחה</strong> - הוא מושמע וכל מקש מפנה לשאלון או לשלוחה. אחרת, אם
              יש כמה שאלונים פעילים, יוצע ללקוח לבחור.
            </li>
            <li>
              המערכת בודקת את המחזור הנוכחי. אם כבר נקלטה רשומה - תושמע הודעה מתאימה והשיחה תנותב לפי
              ההגדרה.
            </li>
            <li>השאלות מוקראות אחת אחרי השנייה. אישור קלט (אם מוגדר).</li>
            <li>
              בסיום מושמעת הודעת הסיום, ואז לפי <strong>"יעד לאחר שאלון"</strong> - ניתוק, חזרה לשלוחה
              ראשית, או מעבר לשלוחה ספציפית.
            </li>
          </ol>
          <Separator />
          <p className="text-xs">
            כל הרשומות נצברות באופן אוטומטי בעמוד "רשומות" וניתנות לייצוא לאקסל.
          </p>
        </Section>

        <div className="text-center text-sm text-muted-foreground py-4">
          <Link to="/dashboard" className="text-primary hover:underline">
            חזרה ללוח הבקרה
          </Link>
        </div>
      </div>
    </div>
  );
}
